import React, { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery } from 'urql';
import { graphql } from '../../gql';
import {
  Grid,
  GridColumn,
  GridFilterChangeEvent,
  GridHeaderSelectionChangeEvent,
  GridNoRecords,
  GridSelectionChangeEvent,
  GridSortChangeEvent,
} from '@progress/kendo-react-grid';
import { SelectDescriptor } from '@progress/kendo-react-data-tools';
import { getter } from '@progress/kendo-react-common';
import { Link } from '@tanstack/react-router';
import { PageTitle } from '../PageTitle.tsx';
import { HeroIcon } from '../../shared/HeroIcon.tsx';
import {
  CompositeFilterDescriptor,
  SortDescriptor,
} from '@progress/kendo-data-query';
import { SortDirection, UserOrLecturerSortField } from '../../gql/graphql.ts';
import { Loader } from '@progress/kendo-react-indicators';
import { Chip } from '@progress/kendo-react-buttons';
import { privilegesToRoleDisplayName } from '../../public/permissions.ts';
import { describeUserBackendError } from './errorTranslations.ts';
import { Alert } from '../../shared/Alert.tsx';

interface UsersPageProps {
  companyId: string;
}

const UsersQuery = graphql(/* GraphQL */ `
  query usersAndLecturers(
    $companyId: String!
    $skip: Int!
    $limit: Int!
    $email: String
    $firstName: String
    $lastName: String
    $type: UserOrLecturerType
    $sortField: UserOrLecturerSortField
    $sortDirection: SortDirection
  ) {
    usersAndLecturersForCompany(
      companyId: $companyId
      skip: $skip
      limit: $limit
      email: $email
      firstName: $firstName
      lastName: $lastName
      type: $type
      sortField: $sortField
      sortDirection: $sortDirection
    ) {
      items {
        lecturerId
        userId
        jobTitle
        hasAccount
        privileges
        passwordSetupPending
        caseTwinHasUser
        address {
          id
          email
          firstName
          lastName
        }
      }
      count
    }
  }
`);

const DeleteUserMutation = graphql(/* GraphQL */ `
  mutation deleteUser($companyId: String!, $userId: String!) {
    deleteUser(companyId: $companyId, userId: $userId)
  }
`);

const InviteUsersMutation = graphql(/* GraphQL */ `
  mutation inviteUsers($companyId: String!, $addressIds: [String!]!) {
    inviteUsers(companyId: $companyId, addressIds: $addressIds)
  }
`);

const ResendInvitationMutation = graphql(/* GraphQL */ `
  mutation resendInvitation($companyId: String!, $userId: String!) {
    resendInvitation(companyId: $companyId, userId: $userId)
  }
`);

// Helper function to map Kendo field names to backend sort fields
const mapFieldToSortField = (
  field: string,
): UserOrLecturerSortField | undefined => {
  switch (field) {
    case 'address.email':
      return UserOrLecturerSortField.Email;
    case 'address.firstName':
      return UserOrLecturerSortField.Firstname;
    case 'address.lastName':
      return UserOrLecturerSortField.Lastname;
    default:
      return undefined;
  }
};

// Helper function to extract filter value from CompositeFilterDescriptor
const getFilterValue = (
  filter: CompositeFilterDescriptor,
  field: string,
): string | undefined => {
  for (const f of filter.filters) {
    if ('field' in f && f.field === field && f.value) {
      return String(f.value);
    }
  }
  return undefined;
};

export const UsersPage: React.FC<UsersPageProps> = ({ companyId }) => {
  const [page, setPage] = useState({
    skip: 0,
    take: 10,
  });

  const [filter, setFilter] = useState<CompositeFilterDescriptor>({
    logic: 'and',
    filters: [],
  });

  const [sort, setSort] = useState<SortDescriptor[]>([]);

  // Extract filter values for backend query
  const emailFilter = getFilterValue(filter, 'address.email');
  const firstNameFilter = getFilterValue(filter, 'address.firstName');
  const lastNameFilter = getFilterValue(filter, 'address.lastName');

  // Map sort to backend parameters
  const sortField =
    sort.length > 0 ? mapFieldToSortField(sort[0].field) : undefined;
  const sortDirection =
    sort.length > 0
      ? sort[0].dir === 'asc'
        ? SortDirection.Asc
        : SortDirection.Desc
      : undefined;

  // TODO: Error handling
  const [{ data, fetching }, reExecuteUsersQuery] = useQuery({
    query: UsersQuery,
    variables: {
      companyId,
      skip: page.skip,
      limit: page.take,
      email: emailFilter,
      firstName: firstNameFilter,
      lastName: lastNameFilter,
      sortField,
      sortDirection,
    },
    pause: companyId === undefined || companyId === null,
  });

  const [spinnerRowIndex, setSpinnerRowIndex] = useState<number | null>(null);
  const [selectedState, setSelectedState] = useState<SelectDescriptor>({});
  const [inviteError, setInviteError] = useState<string | null>(null);

  const [, executeDeleteUser] = useMutation(DeleteUserMutation);
  const [inviteUsersResult, executeInviteUsers] =
    useMutation(InviteUsersMutation);
  const [resendInvitationResult, executeResendInvitation] = useMutation(
    ResendInvitationMutation,
  );

  const idGetter = getter('address.id');

  const onSelectionChange = (event: GridSelectionChangeEvent) => {
    setSelectedState(event.select);
  };

  const onHeaderSelectionChange = useCallback(
    (event: GridHeaderSelectionChangeEvent) => {
      const checkboxElement: HTMLInputElement = event.syntheticEvent
        .target as HTMLInputElement;
      const checked = checkboxElement.checked as boolean;
      const newSelectedState: Record<string, boolean> = {};

      event.dataItems.forEach((item) => {
        newSelectedState[idGetter(item)] = checked;
      });
      setSelectedState(newSelectedState);
    },
    [idGetter],
  );

  // Data is now filtered/paginated/sorted by the backend
  const items = data?.usersAndLecturersForCompany?.items ?? [];
  const totalCount = data?.usersAndLecturersForCompany?.count ?? 0;

  // Addresses can collide on email casing (BTS desktop sync vs. web-created
  // entities). The backend refuses to attach a User to a case-twin address
  // when one already has a User and reports caseTwinHasUser per item; mirror
  // that here so the invite button is visibly disabled instead of failing on
  // click. The flag is computed across the whole company, not just the page.
  // Precomputed as a Set so the per-row render cells and selection checks
  // are O(1) lookups instead of O(n) `items.find()` scans.
  const caseTwinUserAddressIds = useMemo(() => {
    const set = new Set<string>();
    for (const i of items) {
      if (i?.caseTwinHasUser && i.address?.id) set.add(i.address.id);
    }
    return set;
  }, [items]);
  const addressIdHasCaseTwinUser = (addressId: string) =>
    caseTwinUserAddressIds.has(addressId);

  const onSortChange = (event: GridSortChangeEvent) => {
    setSort(event.sort);
    // Reset to first page when sorting changes
    setPage({ ...page, skip: 0 });
  };

  const onFilterChange = (e: GridFilterChangeEvent) => {
    setFilter(e.filter ?? { logic: 'and', filters: [] });
    // Reset to first page when filter changes
    setPage({ ...page, skip: 0 });
  };

  const deleteUser = (userId: string) => {
    executeDeleteUser({ companyId, userId }).then(() => reExecuteUsersQuery());
  };

  const inviteUsers = (addressIds: string[]) => {
    setInviteError(null);
    return executeInviteUsers({ companyId, addressIds }).then((result) => {
      if (result.error) {
        setInviteError(
          describeUserBackendError(result.error, 'Einladung fehlgeschlagen.'),
        );
        return;
      }
      reExecuteUsersQuery();
    });
  };

  const resendInvitation = (userId: string) => {
    return executeResendInvitation({ companyId, userId }).then(() =>
      reExecuteUsersQuery(),
    );
  };

  return (
    <>
      <PageTitle title="Personen" />

      {inviteError && (
        <Alert type="error" icon="ExclamationTriangle" className="mb-4">
          <div className="flex flex-1 items-start justify-between gap-4">
            <span>{inviteError}</span>
            <button
              type="button"
              className="text-red-700 hover:text-red-900"
              onClick={() => setInviteError(null)}
              aria-label="Meldung schließen"
            >
              ×
            </button>
          </div>
        </Alert>
      )}

      <div className={'mb-4 flex justify-end'}>
        <Link
          className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary"
          to={'/admin/users/new'}
        >
          <HeroIcon name="UserPlus" className="h-5 w-5" />
          Neuer Benutzer
        </Link>
      </div>

      <Grid
        className="h-[653px] [&_.k-grid-norecords-template]:border-0"
        showLoader={fetching}
        data={items}
        dataItemKey="address.id"
        onFilterChange={onFilterChange}
        onSelectionChange={onSelectionChange}
        selectable={{ enabled: true, mode: 'multiple' }}
        select={selectedState}
        onHeaderSelectionChange={onHeaderSelectionChange}
        filter={filter}
        filterOperators={{
          text: [{ operator: 'contains', text: 'grid.filterContainsOperator' }],
        }}
        sort={sort}
        onSortChange={onSortChange}
        take={page?.take}
        skip={page?.skip}
        total={totalCount}
        onPageChange={(event) => {
          setPage(event.page);
        }}
        filterable
        sortable
        pageable
      >
        <GridNoRecords>
          {fetching ? ' ' : 'Keine Personen gefunden'}
        </GridNoRecords>
        <GridColumn filterable={false} columnType="checkbox" />
        <GridColumn field="address.email" title="Email" />
        <GridColumn field="address.firstName" title="Vorname" />
        <GridColumn field="address.lastName" title="Nachname" />
        <GridColumn
          filterable={false}
          sortable={false}
          title="Typ"
          width={'150px'}
          cells={{
            data: (props) => {
              let userType;
              if (!props.dataItem.hasAccount) {
                userType = 'Dozent (BTS)';
                if (!props.dataItem.lecturerId) {
                  userType = 'Addresse (BTS)';
                }
              } else {
                userType = privilegesToRoleDisplayName(
                  props.dataItem.privileges,
                );
              }
              return <td>{userType && <Chip text={userType} />}</td>;
            },
          }}
        />
        <GridColumn
          filterable={true}
          sortable={false}
          title="Aktionen"
          width={'170px'}
          cells={{
            filterCell: () => {
              const selectionHasUser = Object.entries(selectedState).some(
                ([key, value]) => {
                  if (value) {
                    return Boolean(
                      items.find((item) => item?.address?.id === key)?.userId,
                    );
                  }
                  return false;
                },
              );
              const selectionHasCaseTwinUser = Object.entries(
                selectedState,
              ).some(([key, value]) => value && addressIdHasCaseTwinUser(key));

              const selectionCount =
                Object.values(selectedState).filter(Boolean).length;

              const selectionIsEmpty = selectionCount === 0;

              const buttonDisabled =
                selectionIsEmpty ||
                selectionHasUser ||
                selectionHasCaseTwinUser ||
                inviteUsersResult.fetching;

              return (
                <td className="flex h-[38px] w-full items-center gap-2 leading-[38px]">
                  <button
                    className={`k-button k-button-md ${selectionCount > 0 ? 'k-button-solid k-button-solid-secondary' : 'k-button-outline k-button-outline-secondary'} k-rounded-md ${buttonDisabled ? 'k-disabled' : ''}`}
                    onClick={() => {
                      const inviteeAddressIds = Object.entries(selectedState)
                        .map(([key, value]) => (value ? key : null))
                        .filter(Boolean) as string[];

                      setSpinnerRowIndex(0);

                      inviteUsers(inviteeAddressIds).then(() => {
                        setSpinnerRowIndex(null);
                        setSelectedState({});
                      });
                    }}
                    title="Einladung an alle verschicken, um ein Konto zu erstellen"
                    disabled={buttonDisabled}
                  >
                    {inviteUsersResult.fetching &&
                    spinnerRowIndex !== null &&
                    spinnerRowIndex === 0 ? (
                      <Loader
                        size="small"
                        type={'infinite-spinner'}
                        className={'!text-bts-theme-text-lighter'}
                      />
                    ) : (
                      <HeroIcon name="Envelope" className="block h-5 w-5" />
                    )}
                  </button>
                  {Boolean(selectionCount) && <p>({selectionCount})</p>}
                </td>
              );
            },
            data: (props) => {
              const selectionCount =
                Object.values(selectedState).filter(Boolean).length;
              return (
                <td className={'k-button-link-secondary flex h-full space-x-2'}>
                  {props.dataItem.hasAccount ? (
                    <>
                      <div
                        className={`k-button k-button-md k-button-outline k-button-outline-secondary k-rounded-md !text-[#6c757d] hover:!text-white ${props.dataItem.hasAccount ? '' : 'k-disabled'}`}
                      >
                        <Link
                          to={
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            `/admin/users/edit/${props.dataItem.userId}` as any
                          }
                          title="Benutzer bearbeiten"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <HeroIcon name="Pencil" className="block h-5 w-5" />
                        </Link>
                      </div>

                      {props.dataItem.passwordSetupPending && (
                        <button
                          className={`k-button k-button-md k-button-outline k-button-outline-secondary k-rounded-md ${resendInvitationResult.fetching ? 'k-disabled' : ''}`}
                          onClick={() => {
                            setSpinnerRowIndex(props.dataIndex + 1);
                            resendInvitation(props.dataItem.userId).then(() => {
                              setSpinnerRowIndex(null);
                            });
                          }}
                          title="Einladung erneut versenden"
                          disabled={resendInvitationResult.fetching}
                        >
                          {resendInvitationResult.fetching &&
                          spinnerRowIndex &&
                          props.dataIndex === spinnerRowIndex - 1 ? (
                            <Loader
                              size="small"
                              type={'infinite-spinner'}
                              className={'!text-bts-theme-text-lighter'}
                            />
                          ) : (
                            <HeroIcon
                              name="ArrowPath"
                              className="block h-5 w-5"
                            />
                          )}
                        </button>
                      )}

                      <button
                        className="k-button k-button-md k-button-outline k-button-outline-secondary k-rounded-md"
                        onClick={() => deleteUser(props.dataItem.userId)}
                        title="Benutzer löschen"
                      >
                        <HeroIcon name="Trash" className="block h-5 w-5" />
                      </button>
                    </>
                  ) : (
                    (() => {
                      const hasCaseTwinUser = addressIdHasCaseTwinUser(
                        props.dataItem.address.id,
                      );
                      const rowDisabled =
                        inviteUsersResult.fetching ||
                        selectionCount !== 0 ||
                        hasCaseTwinUser;
                      const tooltip = hasCaseTwinUser
                        ? 'Eine andere Adresse mit derselben E-Mail (Groß-/Kleinschreibung) hat bereits ein Konto. Pro E-Mail kann nur eine Adresse eingeladen werden.'
                        : 'Einzelne Einladung verschicken um ein Konto zu erstellen';
                      return (
                        <span title={tooltip} className="inline-block">
                          <button
                            className={`k-button k-button-md k-button-outline k-button-outline-secondary k-rounded-md ${rowDisabled ? 'k-disabled' : ''}`}
                            onClick={() => {
                              setSpinnerRowIndex(props.dataIndex + 1);
                              inviteUsers([props.dataItem.address.id]).then(
                                () => {
                                  setSpinnerRowIndex(null);
                                },
                              );
                            }}
                            disabled={rowDisabled}
                          >
                            {inviteUsersResult.fetching &&
                            spinnerRowIndex &&
                            props.dataIndex === spinnerRowIndex - 1 ? (
                              <Loader
                                size="small"
                                type={'infinite-spinner'}
                                className={'!text-bts-theme-text-lighter'}
                              />
                            ) : (
                              <HeroIcon
                                name="Envelope"
                                className="block h-5 w-5"
                              />
                            )}
                          </button>
                        </span>
                      );
                    })()
                  )}
                </td>
              );
            },
          }}
        />
      </Grid>
    </>
  );
};
